import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useLocale } from '@/hooks/use-locale'

export const ARC_NAV_ID = 'story-arcs'

export type JumpArc = {
    id: string
    number: number
    title: string
}

interface ArcJumpNavProps {
    arcs: JumpArc[]
    id?: string
    className?: string
}

const shortTitle = (title: string): string => title.split(':')[0].trim() || title

const ArcJumpNav: FC<ArcJumpNavProps> = ({
    arcs,
    id = ARC_NAV_ID,
    className
}): ReactNode => {
    const { locale, t } = useLocale()
    const latin = locale === 'en'
    const labelId = `${id}-label`

    if (arcs.length === 0) return null

    return (
        <nav id={id} aria-labelledby={labelId} className={cn('scroll-mt-24', className)}>
            <p id={labelId} className={cn('flex items-center gap-3 font-medium text-gold-soft', latin ? 'text-xs uppercase tracking-[0.2em]' : 'text-sm')}>
                {t.storyJumpLabel}
                <span aria-hidden='true' className='h-px w-16 bg-linear-to-r from-gold/70 to-transparent' />
            </p>

            <ol role='list' className='mt-4 flex flex-wrap gap-2 sm:gap-2.5'>
                {arcs.map(arc => (
                    <li key={arc.id} className='max-w-full'>
                        <a
                            href={`#${arc.id}`}
                            className={cn(
                                'group inline-flex min-h-11 max-w-full items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.05] py-1.5 pl-1.5 pr-4 text-left text-sm font-medium text-white/85',
                                'transition-colors hover:border-gold/60 hover:bg-white/10 hover:text-white motion-reduce:transition-none',
                                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                            )}
                        >
                            <span
                                aria-hidden='true'
                                className='inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-gold/15 font-heading text-base font-semibold text-gold-soft lining-nums transition-colors group-hover:bg-gold group-hover:text-night motion-reduce:transition-none'
                            >
                                {arc.number}
                            </span>

                            {' '}

                            <span className='min-w-0'>
                                <span className='sr-only'>
                                    {t.arcNumber.replace('{number}', String(arc.number))}:
                                </span>

                                {' '}

                                {shortTitle(arc.title)}
                            </span>
                        </a>
                    </li>
                ))}
            </ol>
        </nav>
    )
}

export default ArcJumpNav
